import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
  Refresh as RefreshIcon,
  ManageAccounts as ManageIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useQuery } from '@apollo/client';
import { useAuth } from '../contexts/AuthContext';
import { useSchedule } from '../hooks/useSchedule';
import { Staff } from '../types';
import { GET_STORE_BY_ID } from '../graphql/queries';
import ScheduleSummary from '../components/schedule/ScheduleSummary';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ScheduleEditDialog from '../components/schedule/ScheduleEditDialog';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const Schedule = () => {
  const theme = useTheme();
  const { storeId: authStoreId, loading: authLoading } = useAuth();
  const { storeId: urlStoreId } = useParams<{ storeId: string }>();
  
  // Use storeId from URL if available, otherwise fall back to auth context
  const storeId = urlStoreId || authStoreId;
  
  const [combinedScheduleDialog, setCombinedScheduleDialog] = useState(false);
  const [dialogInitialData, setDialogInitialData] = useState<any>(null);
  const [dialogMode, setDialogMode] = useState<'edit' | 'create'>('create');

  // Use the custom hook - only initialize when storeId is available
  const {
    staff,
    loading: scheduleLoading,
    error,
    currentWeek,
    weekDates,
    setCurrentWeek,
    createWeeklySchedule,
    createRecurringSchedule,
    getStaffScheduleForWeek,
    calculateWorkHours,
    formatTime,
    refetchData,
  } = useSchedule({ storeId: storeId || '' });

  // Get store data for operating hours
  const { data: storeData } = useQuery(GET_STORE_BY_ID, {
    variables: { storeId: storeId },
    skip: !storeId,
  });

  // Helper function to get default store hours based on day of week
  const getDefaultStoreHours = (isWeekend: boolean = false) => {
    const store = storeData?.storeDetails;
    if (!store) {
      return { startTime: '09:00', endTime: '17:00' };
    }
    
    return {
      startTime: isWeekend ? store.weekendStartTime : store.weekdayStartTime,
      endTime: isWeekend ? store.weekendEndTime : store.weekdayEndTime
    };
  };

  // Helper function to build initial data for editing existing schedules
  const buildInitialDataForStaff = (staffMember: Staff) => {
    const staffSchedules = getStaffScheduleForWeek(staffMember);
    const hasAnySchedule = Object.values(staffSchedules).some(schedule => schedule !== null);
    
    // Calculate the current week start date based on currentWeek offset
    const currentWeekStart = new Date();
    currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay() + 1 + (currentWeek * 7));
    
    // Get default store hours (using weekday hours as default)
    const defaultHours = getDefaultStoreHours();
    
    if (!hasAnySchedule) {
      // No existing schedule, return initial data for creation
      return {
        selectedStaffId: staffMember.id,
        scheduleType: 'single',
        startTime: defaultHours.startTime,
        endTime: defaultHours.endTime,
        startBreak: '12:00',
        endBreak: '13:00',
        hasBreak: false,
        recurringWeeks: 1,
        selectedWeek: currentWeekStart, // Use current week from parent component
        weeklySchedule: buildWeeklyScheduleFromStaffSchedules(staffSchedules)
      };
    }
    
    // Has existing schedule, build edit data
    return {
      selectedStaffId: staffMember.id,
      scheduleType: 'single',
      startTime: defaultHours.startTime,
      endTime: defaultHours.endTime,
      startBreak: '12:00',
      endBreak: '13:00',
      hasBreak: false,
      recurringWeeks: 1,
      selectedWeek: currentWeekStart, // Use current week from parent component
      weeklySchedule: buildWeeklyScheduleFromStaffSchedules(staffSchedules)
    };
  };

  // Helper function to convert staff schedules to weekly schedule format
  const buildWeeklyScheduleFromStaffSchedules = (staffSchedules: any) => {
    const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const weeklySchedule: any = {};
    
    // Calculate the current week start date based on currentWeek offset
    const currentWeekStart = new Date();
    currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay() + 1 + (currentWeek * 7));
    
    dayKeys.forEach((dayKey, index) => {
      // Calculate the date for this day based on current week
      const dayDate = new Date(currentWeekStart);
      dayDate.setDate(dayDate.getDate() + index);
      const dateKey = dayDate.toISOString().split('T')[0];
      
      const schedule = staffSchedules[dateKey];
      
      if (schedule) {
        weeklySchedule[dayKey] = {
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          breakStart: schedule.startBreak,
          breakEnd: schedule.endBreak,
          hasBreak: !!(schedule.startBreak && schedule.endBreak),
          isActive: true
        };
      } else {
        weeklySchedule[dayKey] = {
          startTime: '09:00',
          endTime: '17:00',
          breakStart: '12:00',
          breakEnd: '13:00',
          hasBreak: false,
          isActive: false
        };
      }
    });
    
    return weeklySchedule;
  };

  // Function to open dialog for specific staff member
  const openDialogForStaff = (staffMember: Staff) => {
    const initialData = buildInitialDataForStaff(staffMember);
    
    setDialogInitialData(initialData);
    setDialogMode('create'); // Always use create mode for the unified dialog
    setCombinedScheduleDialog(true);
  };

  // Handle combined schedule creation/editing
  const handleCombinedScheduleCreation = async (data: any) => {
    if (!storeId) {
      console.error('Store ID is not available');
      return;
    }

    try {
      if (data.scheduleType === 'recurring') {
        // Handle recurring schedule creation
        // Create times array for all 7 days (Monday = 0, Sunday = 6)
        const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const times = dayKeys.map(dayKey => {
          const isDaySelected = data.selectedDays.includes(dayKey);
          if (isDaySelected) {
            return {
              startTime: data.startTime,
              endTime: data.endTime,
              startBreak: data.hasBreak ? data.startBreak : null,
              endBreak: data.hasBreak ? data.endBreak : null,
            };
          } else {
            return {
              startTime: null,
              endTime: null,
              startBreak: null,
              endBreak: null,
            };
          }
        });

        console.log('Creating recurring schedule:', {
          staffId: data.selectedStaffId,
          recurringWeeks: data.recurringWeeks,
          selectedDays: data.selectedDays,
          times: times,
          activeDays: times.filter(t => t.startTime !== null).length
        });

        const result = await createRecurringSchedule(
          data.selectedStaffId,
          times,
          data.recurringWeeks
        );

        if (result.data?.createWeeklySchedule?.ok) {
          setCombinedScheduleDialog(false);
          refetchData();
        }
      } else {
        // Handle single week schedule creation
        // Use the times array from the dialog data if available, otherwise construct it
        let times;
        if (data.times && data.times.length === 7) {
          // Use the times array directly from the dialog
          times = data.times.map((timeEntry: any) => ({
            startTime: timeEntry.startTime,
            endTime: timeEntry.endTime,
            startBreak: timeEntry.startBreak || null,
            endBreak: timeEntry.endBreak || null,
          }));
        } else {
          // Fallback: construct times from weeklySchedule data
          const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          times = dayKeys.map(dayKey => {
            const scheduleData = data.weeklySchedule?.[dayKey];
            if (!scheduleData || !scheduleData.isActive) {
              return { startTime: null, endTime: null, startBreak: null, endBreak: null };
            }
            return {
              startTime: scheduleData.startTime,
              endTime: scheduleData.endTime,
              startBreak: scheduleData.hasBreak ? scheduleData.breakStart || null : null,
              endBreak: scheduleData.hasBreak ? scheduleData.breakEnd || null : null,
            };
          });
        }

        const result = await createWeeklySchedule(data.selectedStaffId || '', times);

        console.log('Creating weekly schedule:', {
          staffId: data.selectedStaffId,
          selectedWeek: data.selectedWeek,
          times: times,
          weeklySchedule: data.weeklySchedule
        });

        if (result.data?.createWeeklySchedule?.ok) {
          setCombinedScheduleDialog(false);
          refetchData();
        }
      }
    } catch (error) {
      console.error('Error creating schedule:', error);
    }
  };

  if (authLoading || scheduleLoading) {
    return <LoadingSpinner message="Loading schedule..." />;
  }

  if (!storeId) {
    return (
      <Box sx={{ 
        p: { xs: 2, md: 3 }, 
        maxWidth: '1200px', 
        mx: 'auto',
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}>
        <Alert severity="warning">
          No store ID found. Please ensure you are properly authenticated and have access to a store.
          <br />
          <small>URL Store ID: {urlStoreId || 'None'}</small>
          <br />
          <small>Auth Store ID: {authStoreId || 'None'}</small>
        </Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        p: { xs: 2, md: 3 }, 
        maxWidth: '1200px', 
        mx: 'auto',
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}>
        <Alert severity="error">
          Error loading data: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      maxWidth: '1200px', 
      mx: 'auto',
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 1 }}>
              Schedule Management
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              Manage staff schedules, set working hours, and organize shifts
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                refetchData();
              }}
              sx={{ 
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setDialogInitialData(null);
                setDialogMode('create');
                setCombinedScheduleDialog(true);
              }}
              sx={{ 
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: theme.shadows[2]
              }}
            >
              Create Schedule
            </Button>
          </Box>
        </Box>

        {/* Week Navigation */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Paper sx={{ 
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentWeek(currentWeek - 1)}
                    disabled={currentWeek <= -4}
                    sx={{ 
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      minWidth: 120
                    }}
                  >
                    Previous Week
                  </Button>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    textAlign: 'center'
                  }}>
                    <CalendarIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      {weekDates[0]?.toLocaleDateString()} - {weekDates[6]?.toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentWeek(currentWeek + 1)}
                    disabled={currentWeek >= 4}
                    sx={{ 
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      minWidth: 120
                    }}
                  >
                    Next Week
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          {/* Schedule Summary */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ScheduleSummary
                staff={staff}
                weekDates={weekDates}
                getStaffScheduleForWeek={getStaffScheduleForWeek}
                calculateWorkHours={calculateWorkHours}
              />
            </motion.div>
          </Grid>

          {/* Staff Schedule Grid */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Paper sx={{ 
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Staff Schedules
                  </Typography>
                </Box>
                
                {(!staff || staff.length === 0) ? (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      borderRadius: 1.5,
                      bgcolor: 'info.light',
                      '& .MuiAlert-message': { fontSize: '1rem' }
                    }}
                  >
                    No staff members found. Please add staff members first.
                  </Alert>
                ) : (
                  <TableContainer sx={{ 
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.default'
                  }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Staff Member</TableCell>
                          {DAYS_OF_WEEK.map((day, index) => (
                            <TableCell key={day} align="center" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600}>{day}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {weekDates[index]?.toLocaleDateString()}
                                </Typography>
                              </Box>
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {staff?.map((staffMember: Staff) => {
                          const staffSchedules = getStaffScheduleForWeek(staffMember);
                          const totalHours = Object.values(staffSchedules).reduce((total, schedule) => {
                            return total + calculateWorkHours(
                              schedule.startTime,
                              schedule.endTime,
                              schedule.startBreak,
                              schedule.endBreak
                            );
                          }, 0);

                          return (
                            <TableRow key={staffMember.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Box
                                    sx={{
                                      p: 1,
                                      borderRadius: 1.5,
                                      backgroundColor: theme.palette.primary.main,
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minWidth: 32,
                                      height: 32
                                    }}
                                  >
                                    <PersonIcon fontSize="small" />
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                      {staffMember.fullName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {totalHours.toFixed(1)} hours/week
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              {DAYS_OF_WEEK.map((day, index) => {
                                const dateKey = weekDates[index]?.toISOString().split('T')[0];
                                const schedule = staffSchedules[dateKey];
                                
                                return (
                                  <TableCell key={day} align="center" sx={{ p: 1 }}>
                                    {schedule ? (
                                      <Box sx={{ 
                                        p: 1.5, 
                                        border: 1,
                                        borderColor: 'success.main',
                                        borderRadius: 1.5,
                                        bgcolor: alpha(theme.palette.success.main, 0.05),
                                        minHeight: 60,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                      }}>
                                        <Typography variant="body2" fontWeight={600} color="success.main">
                                          {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                                        </Typography>
                                        {schedule.startBreak && schedule.endBreak && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            Break: {formatTime(schedule.startBreak)} - {formatTime(schedule.endBreak)}
                                          </Typography>
                                        )}
                                        <Typography variant="caption" color="success.main" fontWeight={600}>
                                          {calculateWorkHours(schedule.startTime, schedule.endTime, schedule.startBreak, schedule.endBreak).toFixed(1)}h
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Box sx={{ 
                                        p: 1.5, 
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        bgcolor: 'background.default',
                                        minHeight: 60,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                      }}>
                                        <Typography variant="body2" color="text.secondary">
                                          No schedule
                                        </Typography>
                                      </Box>
                                    )}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                  {(() => {
                                    const hasAnySchedule = Object.values(staffSchedules).some(schedule => schedule !== null);

                                    return (
                                      <Tooltip title="Manage Schedule">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            openDialogForStaff(staffMember);
                                          }}
                                          sx={{
                                            bgcolor: hasAnySchedule ? 'success.main' : 'primary.main',
                                            color: 'white',
                                            '&:hover': {
                                              bgcolor: hasAnySchedule ? 'success.dark' : 'primary.dark',
                                              color: 'white',
                                              transform: 'scale(1.05)'
                                            }
                                          }}
                                        >
                                          <ManageIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    );
                                  })()}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

      </motion.div>

      {/* Combined Schedule Dialog */}
      <ScheduleEditDialog
        open={combinedScheduleDialog}
        onClose={() => {
          setCombinedScheduleDialog(false);
          setDialogInitialData(null);
          setDialogMode('create');
        }}
        onSave={handleCombinedScheduleCreation}
        mode={dialogMode}
        initialData={dialogInitialData}
        staffList={staff?.map(s => ({ id: s.id, fullName: s.fullName })) || []}
        currentWeek={currentWeek}
        getStaffScheduleForWeek={getStaffScheduleForWeek}
        storeData={storeData?.storeDetails}
      />
    </Box>
  );
};

export default Schedule;
